import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://eventshowcase.de';

const RESOURCE_CATEGORIES = [
  {
    label: 'Getting Started',
    icon: 'ri-rocket-line',
    color: 'bg-teal-50 text-teal-600',
    resources: [
      { title: 'How to Create Your First Event', type: 'Guide', link: '/creator-guide', icon: 'ri-file-text-line' },
      { title: 'Understanding Pricing & Commission', type: 'Guide', link: '/subscribe', icon: 'ri-percent-line' },
      { title: 'How It Works — Full Overview', type: 'Guide', link: '/how-it-works', icon: 'ri-information-line' },
      { title: 'Setting Up Your Organizer Profile', type: 'Guide', link: '/dashboard/profile', icon: 'ri-user-settings-line' },
    ],
  },
  {
    label: 'Growing Your Audience',
    icon: 'ri-user-add-line',
    color: 'bg-amber-50 text-amber-600',
    resources: [
      { title: 'Social Follow Gate — Complete Guide', type: 'Guide', link: '/creator-guide', icon: 'ri-instagram-line' },
      { title: 'Email Campaign Best Practices', type: 'Guide', link: '/creator-guide', icon: 'ri-mail-send-line' },
      { title: 'How to Promote Your Event', type: 'Tips', link: '/creator-guide', icon: 'ri-megaphone-line' },
      { title: 'Success Stories from Top Organizers', type: 'Inspiration', link: '/success-stories', icon: 'ri-star-line' },
    ],
  },
  {
    label: 'Monetization',
    icon: 'ri-money-dollar-circle-line',
    color: 'bg-green-50 text-green-600',
    resources: [
      { title: 'Ticket Pricing Strategy Guide', type: 'Guide', link: '/creator-guide', icon: 'ri-price-tag-3-line' },
      { title: 'Setting Up Your Online Store', type: 'Guide', link: '/dashboard/my-store', icon: 'ri-store-3-line' },
      { title: 'Withdrawal & Payout Guide', type: 'Guide', link: '/creator-guide', icon: 'ri-bank-card-line' },
      { title: 'Compare Plans & Save on Commission', type: 'Guide', link: '/subscribe', icon: 'ri-vip-crown-line' },
    ],
  },
  {
    label: 'Tools & Features',
    icon: 'ri-tools-line',
    color: 'bg-gray-50 text-gray-600',
    resources: [
      { title: 'QR Code Check-in System', type: 'Feature', link: '/dashboard/my-events', icon: 'ri-qr-code-line' },
      { title: 'Team Access & Multi-Organizer', type: 'Feature', link: '/dashboard/team', icon: 'ri-team-line' },
      { title: 'Event Analytics Dashboard', type: 'Feature', link: '/dashboard/earnings', icon: 'ri-bar-chart-line' },
      { title: 'Scheduled Email Reminders', type: 'Feature', link: '/creator-guide', icon: 'ri-alarm-line' },
    ],
  },
];

const TYPE_COLORS: Record<string, string> = {
  Guide: 'bg-teal-50 text-teal-600',
  Tips: 'bg-amber-50 text-amber-600',
  Inspiration: 'bg-rose-50 text-rose-600',
  Feature: 'bg-gray-100 text-gray-600',
};

const QUICK_LINKS = [
  { icon: 'ri-add-circle-line', label: 'Create Event', link: '/create-event', color: 'bg-teal-600 text-white' },
  { icon: 'ri-search-line', label: 'Browse Events', link: '/discover', color: 'bg-gray-100 text-gray-700' },
  { icon: 'ri-vip-crown-line', label: 'View Plans', link: '/subscribe', color: 'bg-gray-100 text-gray-700' },
  { icon: 'ri-dashboard-line', label: 'Dashboard', link: '/dashboard', color: 'bg-gray-100 text-gray-700' },
];

export default function ResourcesPage() {
  const { user, profile } = useAuth();
  const [search, setSearch] = useState('');
  const [userPlan, setUserPlan] = useState('starter');
  const [eventsCreated, setEventsCreated] = useState(0);

  const firstName = profile?.full_name?.split(' ')[0] || user?.user_metadata?.full_name?.split(' ')[0] || null;

  useEffect(() => {
    const PAGE_TITLE = 'Resources – Event Organizer Guides & Tools | EVENTSHOWCASE';
    const PAGE_DESC = 'Guides, tips, and tools to help you create better events and grow your audience on EVENTSHOWCASE. From getting started to advanced monetization.';
    const PAGE_URL = `${SITE_URL}/resources`;
    const PAGE_IMG = 'https://public.readdy.ai/ai/img_res/0d2aac08dbb8cf799c72459d1d983389.jpg';

    document.title = PAGE_TITLE;
    if (user) fetchContext();

    const setMeta = (sel: string, attr: string, val: string) => {
      let el = document.querySelector(sel) as HTMLMetaElement | null;
      if (!el) { el = document.createElement('meta'); const [a, v] = sel.replace('meta[', '').replace(']', '').split('='); el.setAttribute(a.trim(), v.replace(/"/g, '').trim()); document.head.appendChild(el); el.setAttribute('data-page-meta', 'true'); }
      el.setAttribute(attr, val);
    };
    setMeta('meta[name="description"]', 'content', PAGE_DESC);
    setMeta('meta[property="og:title"]', 'content', PAGE_TITLE);
    setMeta('meta[property="og:description"]', 'content', PAGE_DESC);
    setMeta('meta[property="og:url"]', 'content', PAGE_URL);
    setMeta('meta[property="og:type"]', 'content', 'website');
    setMeta('meta[property="og:image"]', 'content', PAGE_IMG);
    setMeta('meta[property="og:image:width"]', 'content', '1200');
    setMeta('meta[property="og:image:height"]', 'content', '630');
    setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', 'content', PAGE_TITLE);
    setMeta('meta[name="twitter:description"]', 'content', PAGE_DESC);
    setMeta('meta[name="twitter:image"]', 'content', PAGE_IMG);

    // Schema.org JSON-LD
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Resources - Event Organizer Guides & Tools',
      description: 'Comprehensive resources for event organizers. Guides, tips, and tools to help you create better events and grow your audience.',
      url: `${SITE_URL}/resources`,
      isPartOf: {
        '@type': 'WebSite',
        name: 'EVENTSHOWCASE',
        url: SITE_URL,
      },
      mainEntity: {
        '@type': 'ItemList',
        itemListElement: RESOURCE_CATEGORIES.flatMap((cat, ci) =>
          cat.resources.map((r, ri) => ({
            '@type': 'ListItem',
            position: ci * 10 + ri + 1,
            item: {
              '@type': 'Article',
              headline: r.title,
              description: `${r.type} - ${cat.label}`,
              url: `${SITE_URL}${r.link}`,
            },
          }))
        ),
      },
    };

    const existing = document.getElementById('schema-resources');
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.id = 'schema-resources';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById('schema-resources');
      if (el) el.remove();
      document.querySelectorAll('meta[data-page-meta="true"]').forEach(e => e.remove());
    };
  }, []);

  const fetchContext = async () => {
    if (!user) return;
    const [planRes, eventsRes] = await Promise.all([
      supabase.from('subscriptions').select('plan').eq('user_id', user.id).eq('status', 'active').order('created_at', { ascending: false }).limit(1).maybeSingle(),
      supabase.from('events').select('id', { count: 'exact', head: true }).eq('organizer_id', user.id),
    ]);
    setUserPlan(planRes.data?.plan ?? 'starter');
    setEventsCreated(eventsRes.count || 0);
  };

  const isPaidPlan = userPlan === 'growth' || userPlan === 'pro_partner';

  const allResources = RESOURCE_CATEGORIES.flatMap(cat =>
    cat.resources.map(r => ({ ...r, category: cat.label }))
  );

  const filtered = search.trim()
    ? allResources.filter(r =>
        r.title.toLowerCase().includes(search.toLowerCase()) ||
        r.category.toLowerCase().includes(search.toLowerCase())
      )
    : null;

  return (
    <div className="min-h-screen bg-white">
      <Navbar forceScrolled />

      {/* Hero */}
      <section className="pt-28 pb-14 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            {firstName ? `${firstName}'s Resource Hub` : 'Resources'}
          </h1>
          <p className="text-lg text-gray-500 max-w-xl mx-auto mb-4">
            {firstName ? `Guides and tools tailored for you, ${firstName}.` : 'Guides, tips, and tools to help you create better events and grow your audience.'}
          </p>
          {user && (
            <div className="flex flex-wrap items-center justify-center gap-3 mb-6">
              <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold ${isPaidPlan ? 'bg-teal-100 text-teal-700' : 'bg-gray-200 text-gray-600'}`}>
                <i className="ri-vip-crown-line"></i> {userPlan.replace('_', ' ')} plan
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-gray-100 text-gray-600">
                <i className="ri-calendar-event-line"></i> {eventsCreated} event{eventsCreated !== 1 ? 's' : ''} created
              </span>
              {!isPaidPlan && (
                <Link to="/subscribe" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-teal-600 text-white hover:bg-teal-700 transition-colors whitespace-nowrap">
                  <i className="ri-arrow-up-line"></i> Upgrade plan
                </Link>
              )}
            </div>
          )}
          {/* Search */}
          <div className="max-w-md mx-auto relative">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search resources..."
              className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>
        </div>
      </section>

      {/* Quick links */}
      <section className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex flex-wrap gap-3 justify-center">
          {QUICK_LINKS.map((ql, i) => (
            <Link
              key={i}
              to={ql.link}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors whitespace-nowrap ${ql.color} hover:opacity-90`}
            >
              <i className={ql.icon}></i>
              {ql.label}
            </Link>
          ))}
        </div>
      </section>

      {/* Search results */}
      {filtered && (
        <section className="max-w-5xl mx-auto px-4 pb-8">
          <p className="text-sm text-gray-500 mb-4">{filtered.length} result{filtered.length !== 1 ? 's' : ''} for &ldquo;{search}&rdquo;</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {filtered.map((r, i) => (
              <Link key={i} to={r.link} className="flex items-center gap-3 p-4 border border-gray-100 rounded-xl hover:border-teal-200 hover:bg-teal-50/30 transition-all">
                <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className={`${r.icon} text-gray-600`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{r.title}</p>
                  <p className="text-xs text-gray-400">{r.category}</p>
                </div>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${TYPE_COLORS[r.type]}`}>{r.type}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Categories */}
      {!filtered && (
        <section className="max-w-5xl mx-auto px-4 py-6 pb-16">
          <div className="space-y-10">
            {RESOURCE_CATEGORIES.map((cat, ci) => (
              <div key={ci}>
                <div className="flex items-center gap-3 mb-5">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${cat.color}`}>
                    <i className={`${cat.icon} text-lg`}></i>
                  </div>
                  <h2 className="text-lg font-bold text-gray-900">{cat.label}</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {cat.resources.map((r, ri) => (
                    <Link
                      key={ri}
                      to={r.link}
                      className="flex items-center gap-3 p-4 border border-gray-100 rounded-xl hover:border-teal-200 hover:bg-teal-50/30 transition-all group"
                    >
                      <div className="w-9 h-9 bg-gray-50 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-teal-50 transition-colors">
                        <i className={`${r.icon} text-gray-500 group-hover:text-teal-600 transition-colors`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{r.title}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${TYPE_COLORS[r.type]}`}>{r.type}</span>
                        <i className="ri-arrow-right-line text-gray-300 group-hover:text-teal-500 transition-colors"></i>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
